/**
 * TableOfContents
 * ------------------------
 * Automatically tracks scroll position and highlights the corresponding
 * Table of Contents (TOC) link based on the user's scroll direction and
 * intersection with content sections.
 * 
 * Features:
 * - Smooth scroll behavior when clicking TOC links
 * - Active state management for TOC items
 * - Scroll direction detection (up vs. down)
 * - Smart handling of tall or short sections during scroll
 * - Manual override system to avoid conflicts during TOC clicks
 * - Supports hash-based navigation and initial load state
 * 
 * To use:
 * - Apply `data-id="table-of-contents"` to the <ul> or container element
 * - Ensure each TOC link has an href pointing to a corresponding section ID
 * - Include this script on the page
 */

const TableOfContents = () => {
    const toc = document.querySelector("[data-id='table-of-contents']")
    if (!toc) return
    const tocLinks = toc.querySelectorAll('li a')
    if (!tocLinks.length) return

    const ACTIVE_CLASS = '--active'
    const SCROLL_DEBOUNCE = 100
    const OBSERVER_CONFIG = {
        root: null,
        rootMargin: '0px 0px -50% 0px',
        threshold: 0.5,
    }

    const state = {
        sections: [],
        activeId: null,
        isManualOverride: false,
        isScrollingUp: false,
        lastScrollTop: window.scrollY || document.documentElement.scrollTop,
        observer: null,
        overrideTimeout: null,
        scrollTimeout: null,
    }

    // Initialize sections
    tocLinks.forEach((link) => {
        const href = link.getAttribute('href')
        if (!href?.startsWith('#')) return

        const target = document.querySelector(href)
        if (!target) return

        const id = href.slice(1)
        state.sections.push({ id, link, target, isActive: false })
    })

    if (!state.sections.length) return

    // Utility: Set active section
    const setActiveSection = (id, isManual = false, overrideMs = 500) => {
        if (state.overrideTimeout) clearTimeout(state.overrideTimeout)

        if (isManual) {
            state.isManualOverride = true
            state.overrideTimeout = setTimeout(() => {
                state.isManualOverride = false
            }, overrideMs)
        }

        state.sections.forEach((section) => {
            const isActive = section.id === id
            section.isActive = isActive
            if (isActive) {
                state.activeId = id
                section.link.classList.add(ACTIVE_CLASS)
            } else {
                section.link.classList.remove(ACTIVE_CLASS)
            }
        })
    }

    // Utility: Scroll to section
    const scrollToSection = (id) => {
        const section = state.sections.find((s) => s.id === id)
        if (!section) return

        // Calculate scroll distance
        const currentScroll =
            window.scrollY || document.documentElement.scrollTop
        const targetTop = section.target.offsetTop
        const distance = Math.abs(targetTop - currentScroll)

        // Estimate smooth scroll duration (roughly 1ms per 1px, with min/max bounds)
        const duration = Math.max(500, Math.min(2000, distance))

        section.target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setActiveSection(id, true, duration)
        history.pushState(null, null, `#${id}`)
    }

    // Click handling
    tocLinks.forEach((link) => {
        link.addEventListener('click', (e) => {
            const href = link.getAttribute('href')
            if (!href?.startsWith('#')) return

            const id = href.slice(1)
            const section = state.sections.find((s) => s.id === id)
            if (!section) return

            e.preventDefault()
            scrollToSection(id)
        })
    })

    // Hash change (manual or back button)
    window.addEventListener('hashchange', () => {
        const id = window.location.hash.slice(1)
        if (!id) return
        if (state.activeId === id) return

        const section = state.sections.find((s) => s.id === id)
        if (section) setActiveSection(id, true)
    })

    // Scroll direction tracking
    window.addEventListener('scroll', () => {
        const currentScroll =
            window.scrollY || document.documentElement.scrollTop
        state.isScrollingUp = currentScroll < state.lastScrollTop
        state.lastScrollTop = currentScroll

        if (state.scrollTimeout) clearTimeout(state.scrollTimeout)
        state.scrollTimeout = setTimeout(() => {
            if (state.isManualOverride) {
                state.isManualOverride = false
                if (state.overrideTimeout) clearTimeout(state.overrideTimeout)
            }
        }, SCROLL_DEBOUNCE)
    })

    // Intersection Observer setup
    state.observer = new IntersectionObserver((entries) => {
        if (state.isManualOverride) return

        let best = null
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                best = entry.target
            }
        })

        const current = state.sections.find((s) => s.id === state.activeId)

        // Scroll-up override
        if (state.isScrollingUp && current) {
            const match = entries.find((e) => e.target.id === current.id)
            if (match && !match.isIntersecting) {
                const index = state.sections.findIndex(
                    (s) => s.id === current.id
                )
                if (index > 0) {
                    setActiveSection(state.sections[index - 1].id)
                    return
                }
            }
        }

        // Scroll-down override
        if (!state.isScrollingUp && current) {
            const match = entries.find((e) => e.target.id === current.id)
            if (match && !match.isIntersecting && best) {
                const newId = best.getAttribute('id')
                if (newId && newId !== current.id) {
                    setActiveSection(newId)
                    return
                }
            }
        }

        // Default best match
        if (best) {
            const newId = best.getAttribute('id')
            if (newId && newId !== state.activeId) {
                setActiveSection(newId)
            }
        }
    }, OBSERVER_CONFIG)

    // Observe all section targets
    state.sections.forEach((s) => state.observer.observe(s.target))

    // Handle initial hash on load
    const initialHash = window.location.hash.slice(1)
    const target = state.sections.find((s) => s.id === initialHash)
    if (target) {
        setActiveSection(initialHash, true)
        target.target.scrollIntoView({ behavior: 'auto', block: 'start' })
    } else {
        // Fallback to first visible section
        const scrollTop = window.scrollY || document.documentElement.scrollTop
        for (let i = state.sections.length - 1; i >= 0; i--) {
            const rect = state.sections[i].target.getBoundingClientRect()
            if (rect.top + scrollTop <= scrollTop + window.innerHeight * 0.3) {
                setActiveSection(state.sections[i].id)
                break
            }
        }
    }
}

document.addEventListener('DOMContentLoaded', TableOfContents);
